import React from 'react';
import { Accessibility, CheckCircle, AlertTriangle, XCircle, Eye, Type, Contrast } from 'lucide-react';
import { ColorPalette, hslToHex } from '../utils/colorGenerator';

interface AccessibilityCheckerProps {
  palette: ColorPalette;
}

interface ContrastResult {
  ratio: number;
  level: 'AAA' | 'AA' | 'A' | 'FAIL';
  score: number;
}

export const AccessibilityChecker: React.FC<AccessibilityCheckerProps> = ({ palette }) => {
  // Convert hex to RGB
  const hexToRgb = (hex: string): [number, number, number] => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? [
      parseInt(result[1], 16),
      parseInt(result[2], 16),
      parseInt(result[3], 16)
    ] : [0, 0, 0];
  };

  // Calculate relative luminance
  const getLuminance = (rgb: [number, number, number]): number => {
    const [r, g, b] = rgb.map(c => {
      c = c / 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  };

  // Calculate contrast ratio
  const getContrastRatio = (color1: string, color2: string): ContrastResult => {
    const rgb1 = hexToRgb(hslToHex(color1));
    const rgb2 = hexToRgb(hslToHex(color2));
    
    const lum1 = getLuminance(rgb1);
    const lum2 = getLuminance(rgb2);
    
    const lighter = Math.max(lum1, lum2);
    const darker = Math.min(lum1, lum2);
    
    const ratio = (lighter + 0.05) / (darker + 0.05);
    
    let level: 'AAA' | 'AA' | 'A' | 'FAIL';
    let score: number;
    
    if (ratio >= 7) {
      level = 'AAA';
      score = 100;
    } else if (ratio >= 4.5) {
      level = 'AA';
      score = 80;
    } else if (ratio >= 3) {
      level = 'A';
      score = 60;
    } else {
      level = 'FAIL';
      score = Math.max(0, (ratio / 3) * 40);
    }
    
    return { ratio, level, score };
  };

  // Check all color combinations
  const checkAllCombinations = () => {
    const results: Array<{
      bg: string;
      fg: string;
      bgName: string;
      fgName: string;
      result: ContrastResult;
    }> = [];

    palette.colors.forEach((bgColor, bgIndex) => {
      palette.colors.forEach((fgColor, fgIndex) => {
        if (bgIndex !== fgIndex) {
          const result = getContrastRatio(bgColor, fgColor);
          results.push({
            bg: bgColor,
            fg: fgColor,
            bgName: palette.names[bgIndex],
            fgName: palette.names[fgIndex],
            result
          });
        }
      });
    });

    return results.sort((a, b) => b.result.ratio - a.result.ratio);
  };

  const combinations = checkAllCombinations();
  const bestCombinations = combinations.filter(c => c.result.level === 'AAA' || c.result.level === 'AA');
  const poorCombinations = combinations.filter(c => c.result.level === 'FAIL');

  const getStatusIcon = (level: string) => {
    switch (level) {
      case 'AAA':
        return <CheckCircle className="text-green-500" size={16} />;
      case 'AA':
        return <CheckCircle className="text-blue-500" size={16} />;
      case 'A':
        return <AlertTriangle className="text-yellow-500" size={16} />;
      default:
        return <XCircle className="text-red-500" size={16} />;
    }
  };

  const getStatusColor = (level: string) => {
    switch (level) {
      case 'AAA': return 'text-green-700 bg-green-100';
      case 'AA': return 'text-blue-700 bg-blue-100';
      case 'A': return 'text-yellow-700 bg-yellow-100';
      default: return 'text-red-700 bg-red-100';
    }
  };

  const overallScore = Math.round(
    combinations.reduce((sum, c) => sum + c.result.score, 0) / combinations.length
  );

  return (
    <div className="bg-white/90 backdrop-blur-sm rounded-xl shadow-lg p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
          <Accessibility className="text-white" size={16} />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Accessibility Analysis</h3>
          <p className="text-sm text-gray-600">WCAG 2.1 contrast ratio compliance check</p>
        </div>
        <div className="ml-auto">
          <div className="text-right">
            <div className="text-2xl font-bold text-gray-800">{overallScore}%</div>
            <div className="text-sm text-gray-600">Overall Score</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Best Combinations */}
        <div>
          <h4 className="font-semibold text-gray-800 mb-4 flex items-center space-x-2">
            <CheckCircle className="text-green-500" size={18} />
            <span>Accessible Combinations ({bestCombinations.length})</span>
          </h4>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {bestCombinations.slice(0, 8).map((combo, index) => (
              <div
                key={index}
                className="p-3 rounded-lg border border-gray-200 flex items-center justify-between"
              >
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2">
                    <div
                      className="w-6 h-6 rounded border border-gray-300"
                      style={{ backgroundColor: hslToHex(combo.bg) }}
                    />
                    <span className="text-sm font-medium">{combo.bgName}</span>
                  </div>
                  <Type size={14} className="text-gray-400" />
                  <div className="flex items-center space-x-2">
                    <div
                      className="w-6 h-6 rounded border border-gray-300"
                      style={{ backgroundColor: hslToHex(combo.fg) }}
                    />
                    <span className="text-sm font-medium">{combo.fgName}</span>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-mono text-gray-600">
                    {combo.result.ratio.toFixed(1)}:1
                  </span>
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusColor(combo.result.level)}`}>
                    {combo.result.level}
                  </span>
                  {getStatusIcon(combo.result.level)}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Poor Combinations */}
        <div>
          <h4 className="font-semibold text-gray-800 mb-4 flex items-center space-x-2">
            <XCircle className="text-red-500" size={18} />
            <span>Poor Combinations ({poorCombinations.length})</span>
          </h4>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {poorCombinations.slice(0, 8).map((combo, index) => (
              <div
                key={index}
                className="p-3 rounded-lg border border-red-200 bg-red-50 flex items-center justify-between"
              >
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2">
                    <div
                      className="w-6 h-6 rounded border border-gray-300"
                      style={{ backgroundColor: hslToHex(combo.bg) }}
                    />
                    <span className="text-sm font-medium">{combo.bgName}</span>
                  </div>
                  <Type size={14} className="text-gray-400" />
                  <div className="flex items-center space-x-2">
                    <div
                      className="w-6 h-6 rounded border border-gray-300"
                      style={{ backgroundColor: hslToHex(combo.fg) }}
                    />
                    <span className="text-sm font-medium">{combo.fgName}</span>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-mono text-gray-600">
                    {combo.result.ratio.toFixed(1)}:1
                  </span>
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusColor(combo.result.level)}`}>
                    {combo.result.level}
                  </span>
                  {getStatusIcon(combo.result.level)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Guidelines */}
      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
        <h5 className="font-semibold text-blue-800 mb-2 flex items-center space-x-2">
          <Eye className="text-blue-600" size={16} />
          <span>WCAG Guidelines</span>
        </h5>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <span className="font-medium text-green-700">AAA (7:1)</span>
            <p className="text-blue-700">Enhanced contrast for better accessibility</p>
          </div>
          <div>
            <span className="font-medium text-blue-700">AA (4.5:1)</span>
            <p className="text-blue-700">Standard contrast for normal text</p>
          </div>
          <div>
            <span className="font-medium text-yellow-700">A (3:1)</span>
            <p className="text-blue-700">Minimum for large text (18pt+)</p>
          </div>
        </div>
      </div>
    </div>
  );
};